import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useAuth } from 'contexts/AuthContext';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { useBudgetKindOptions } from 'hooks/useBudgetKindOptions';
import {
  emptyBudgetSearchCodes,
  useBudgetSearchCodeLookup,
} from 'hooks/useBudgetSearchCodeLookup';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { useGridPrintAction } from 'components/grid/print';
import {
  ACCOUNTING_MASTER_CHANGED_EVENT,
  acctClassOptions,
  defaultAcctClassCode,
  acctClassLabel,
  loadAcctClasses,
} from 'budget/budgetAcctStore';
import { filterExpenseRequests, loadExpenseRequests } from 'budget/budgetExpenseRequestStore';

const GRID_ID = 'budget-request-inquiry';

/**
 * 편성요구 마스터×산출내역을 한 행으로 펼친다 (마스터 1건당 디테일 N행)
 * - 디테일이 없는 마스터도 1행 유지 (산출 항목만 빈칸)
 */
function flattenRequestLines(lines = []) {
  const out = [];
  for (const m of lines) {
    if (m.mod === 'D') continue;
    const base = {
      requestUid: m.requestUid,
      deptCode: m.deptCode || '',
      deptName: m.deptName || '',
      projectCode: m.projectCode || '',
      projectName: m.projectName || '',
      subjectCode: m.subjectCode || '',
      subjectDispCode: m.subjectDispCode || m.subjectCode || '',
      subjectName: m.subjectName || '',
      fundName: m.fundName || '',
      budgetAmt: m.budgetAmt ?? 0,
      masterRemark: m.remark || '',
    };
    const details = (m.details || []).filter((d) => d && d.mod !== 'D');
    if (!details.length) {
      out.push({ ...base, id: m.requestUid, calcLevel: '', calcDesc: '', calcBasis: '', calcAmt: '' });
      continue;
    }
    details.forEach((d, i) => {
      out.push({
        ...base,
        id: `${m.requestUid}::${d.detailId || i}`,
        calcLevel: d.calcLevel ?? '',
        calcDesc: d.calcDesc || '',
        calcBasis: d.calcBasis || '',
        calcAmt: d.calcAmt ?? '',
        detailRemark: d.remark || '',
      });
    });
  }
  return out;
}

/**
 * 편성내역조회 — 지출예산편성요구의 마스터·산출내역 결합 조회 (읽기전용)
 */
const BudgetRequestInquiryInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = getCurrentFiscalYear();

  const [acctMasterTick, setAcctMasterTick] = useState(0);
  const classOptions = useMemo(
    () => acctClassOptions(tenantCode),
    [tenantCode, acctMasterTick]
  );
  const defaultAcctClass = useMemo(
    () => defaultAcctClassCode(tenantCode),
    [tenantCode, acctMasterTick]
  );

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      fiscalYear: year,
      budgetKindCode: '',
      acctClass: defaultAcctClass,
      ...emptyBudgetSearchCodes(),
    }),
    [tenantCode, year, defaultAcctClass]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue, watch } = methods;
  const watchedFiscalYear = watch('fiscalYear');
  const { kindOptions, syncKindOptions } = useBudgetKindOptions({
    tenantCode,
    fiscalYear: watchedFiscalYear || year,
    getValues,
    setValue,
    autoSelect: true,
  });

  const getDims = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      acctClass: v.acctClass || defaultAcctClass,
    };
  }, [getValues, tenantCode, year, defaultAcctClass]);

  const { lookupDialog, searchCodeFields } = useBudgetSearchCodeLookup({
    getDims,
    setValue,
    prefsUserId,
  });

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  useEffect(() => {
    if (!tenantCode) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await loadAcctClasses(tenantCode);
      } catch {
        /* 마스터 미적재 시에도 조회는 시도 */
      }
      if (!cancelled) setAcctMasterTick((n) => n + 1);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  useEffect(() => {
    const refresh = (e) => {
      if (e.detail?.tenantCode === tenantCode && e.detail?.axis === 'class') {
        setAcctMasterTick((n) => n + 1);
      }
    };
    window.addEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNTING_MASTER_CHANGED_EVENT, refresh);
  }, [tenantCode]);

  const columns = useMemo(
    () => {
      // 마스터 필드는 산출내역 N행에 걸쳐 동일 → requestUid 단위로 세로 병합
      const mergeMaster = { mergeRows: true, mergeGroupKey: 'requestUid' };
      return [
        hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100, ...mergeMaster }),
        hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 130, ...mergeMaster }),
        hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 100, ...mergeMaster }),
        hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 200, ...mergeMaster }),
        hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 120, ...mergeMaster }),
        hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 180, ...mergeMaster }),
        hiCol.roTextColumn({ header: '재원명', field: 'fundName', size: 90, ...mergeMaster }),
        hiCol.roNumberColumn({
          header: '예산액(천원)',
          field: 'budgetAmt',
          size: 120,
          title: '마스터 예산액 — 동일 편성요구의 산출내역 행에서 병합 표시',
          ...mergeMaster,
        }),
        hiCol.roTextColumn({ header: '산출레벨', field: 'calcLevel', size: 80 }),
        hiCol.roTextColumn({ header: '산출내역', field: 'calcDesc', size: 220 }),
        hiCol.roTextColumn({ header: '산출기초', field: 'calcBasis', size: 200 }),
        hiCol.roNumberColumn({ header: '산출금액(천원)', field: 'calcAmt', size: 130 }),
        hiCol.roTextColumn({ header: '비고', field: 'masterRemark', size: 200, ...mergeMaster }),
      ];
    },
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
  });

  const handleSearch = useCallback(async () => {
    const opts = await syncKindOptions();
    const v = getValues();
    const dims = {
      tenantCode: v.tenantCode || tenantCode,
      fiscalYear: v.fiscalYear || year,
      budgetKindCode: String(v.budgetKindCode || '').trim(),
      acctClass: v.acctClass || defaultAcctClass,
    };
    if (!dims.budgetKindCode) {
      showAlert(
        opts.length
          ? '예산차수(결합코드)를 선택하세요.'
          : '예산차수가 없습니다. 예산차수등록에서 해당 연도의 차수를 등록하세요.',
        'warning'
      );
      return;
    }
    try {
      const lines = await loadExpenseRequests(dims);
      const rows = filterExpenseRequests(flattenRequestLines(lines), {
        deptCode: v.deptCode,
        projectCode: v.projectCode,
        subjectCode: v.subjectCode,
      });
      gridRef.current?.setData?.(rows);
      const kind = `${dims.budgetKindCode} · ${acctClassLabel(dims.acctClass, dims.tenantCode)}`;
      showAlert(`조회했습니다. [${kind}] 편성내역 ${rows.length}건`, 'success');
    } catch (e) {
      showAlert(e?.message || '조회 중 오류가 발생했습니다.', 'error');
    }
  }, [getValues, tenantCode, year, defaultAcctClass, syncKindOptions]);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '편성내역',
    getData,
    getDataColumns,
    joinKeys: ['requestUid'],
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '편성내역조회' });
  const { printButton, dialog: printDialog } = useGridPrintAction({
    fileName: '편성내역조회',
    prefsUserId,
    defaultTitle: '편성내역조회',
  });

  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    printButton,
    excelButton,
  ];

  const searchFields = [
    [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w100,
        labelWidth: SEARCH_LABEL_W,
        required: true,
      },
      {
        name: 'budgetKindCode',
        label: '예산차수',
        type: 'select',
        width: CommonCss.w140,
        labelWidth: SEARCH_LABEL_W,
        options: [{ value: '', label: '선택' }, ...kindOptions],
        required: true,
      },
      {
        name: 'acctClass',
        label: '회계구분',
        type: 'select',
        width: CommonCss.w110,
        labelWidth: SEARCH_LABEL_W,
        options: classOptions,
        required: true,
      },
    ],
    searchCodeFields,
  ];

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel
          storageKey="budget-request-inquiry"
          prefsUserId={prefsUserId}
          summary={`회계연도 ${watchedFiscalYear || year}`}
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>
      <GridExportFocusScope gridId={GRID_ID}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <HiGridComponent
            ref={gridRef}
            columns={columns}
            height="100%"
            showRowNumber
            rowNumberSize={56}
            enableColumnFilters
            enableContextMenu
            enableRowEditMenu={false}
            showModColumn={false}
            enableRowCheck={false}
            enableCut={false}
            enableFindReplace
            allowReplace={false}
            enableHeaderMenu
            enableSelectionStats
            enableFreeze={false}
            enableQuickExport
            exportFileName="편성내역조회"
            gridId={GRID_ID}
            prefsUserId={prefsUserId}
          />
        </div>
      </GridExportFocusScope>
      {excelDialog}
      {printDialog}
      {lookupDialog}
    </PageContainer>
  );
};

const BudgetRequestInquiry = () => (
  <GridExportProvider>
    <BudgetRequestInquiryInner />
  </GridExportProvider>
);

export default BudgetRequestInquiry;
